import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { XStack, YStack, Text } from "tamagui";
import api from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { listAccountOrderHistory } from "../API/handleAccountHistoryAPI";
import { useHazePage, v2 } from "@screen/Home/pages/GasShop/skin";

interface CouponItem {
  customer_coupon_id: string;
  supplier_id: string | null;
}

/**
 * 帳號首頁頂端的三格摘要：可用券／待付金額／本月訂單。
 *
 * 為什麼放在藍色區：那塊漸層原本 200px 只擺一句招呼語，而客戶每次進帳號頁想知道的就是這三個數字。
 * 優惠券因此不再需要獨立成一個區塊（點「可用券」直接進優惠券頁），白卡從四張減成三張。
 *
 * 資料來源：
 * 可用券＝/coupons status=available（平台券 + 目前帳戶 supplier 的帳戶券）；
 * 待付金額＝帳戶頁已打的 /customer/home 回應 widgets.unpaid_bill?.total ?? 0（後端加總），由 props 傳入；
 * 本月訂單＝account-history 取回後在前端計數。
 * 尚未選定身分（xContext 為空）或資料還沒回來時顯示「—」而不是 0，避免謊報。
 */
export const AccountSummary = ({ unpaidTotal }: { unpaidTotal: number | null }) => {
  /* 淡霧頁：原本三格是「白字 + 半透明白底」，靠的是底下有一塊實藍。
     換成淡霧之後那組半透明白等於消失，故改成實白小卡（細邊線 + 深藍數字），
     和頁面下半部的白卡同一種語彙。 */
  const haze = useHazePage();
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const supplierId = chooseIdentity?.primary_supplier?.supplier_id ?? null;
  const xContext = buildXContextHeader(chooseIdentity);

  const [coupons, setCoupons] = useState<number | null>(null);
  const [monthOrders, setMonthOrders] = useState<number | null>(null);

  useEffect(() => {
    if (!cusJwtToken) return;
    let alive = true;

    api
      .get("/app-api/coupons", {
        params: { status: "available", limit: 100, page: 1 },
        headers: { Authorization: `Bearer ${cusJwtToken}` },
      })
      .then((res) => {
        if (!alive) return;
        const items = (res.data?.data?.items ?? []) as CouponItem[];
        setCoupons(items.filter((c) => !c.supplier_id || c.supplier_id === supplierId).length);
      })
      .catch((err) => {
        console.warn("account coupons fetch failed", err);
      });

    if (!xContext) return;
    listAccountOrderHistory({ jwtToken: cusJwtToken, xContext, params: { limit: 50 } })
      .then((res) => {
        if (!alive || res.state !== "success") return;
        const items = res.data.items ?? [];
        const now = new Date();
        setMonthOrders(
          items.filter((it) => {
            if (!it.created_at) return false;
            const d = new Date(it.created_at);
            return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
          }).length,
        );
      })
      .catch((err) => {
        console.warn("account history fetch failed", err);
      });

    return () => {
      alive = false;
    };
  }, [cusJwtToken, xContext, supplierId]);

  const cells: { key: string; label: string; value: string; to: string }[] = [
    { key: "coupons", label: "可用券", value: coupons == null ? "—" : `${coupons} 張`, to: "/account/coupons" },
    { key: "unpaid", label: "待付金額", value: unpaidTotal == null ? "—" : `$${unpaidTotal.toLocaleString("en-US")}`, to: "/orders" },
    { key: "orders", label: "本月訂單", value: monthOrders == null ? "—" : `${monthOrders} 筆`, to: "/orders" },
  ];

  return (
    <XStack gap={8} mt={2}>
      {cells.map((c) => (
        <YStack
          key={c.key}
          flex={1}
          minWidth={0}
          px={10}
          py={9}
          borderRadius={14}
          backgroundColor={haze ? "#fff" : "rgba(255,255,255,0.18)"}
          borderWidth={1}
          borderColor={haze ? v2.cardBorder : "rgba(255,255,255,0.3)"}
          cursor="pointer"
          pressStyle={{ backgroundColor: haze ? "#f2f7fd" : "rgba(255,255,255,0.28)" }}
          onPress={() => navigate(c.to)}>
          <Text fontSize={10.5} color={haze ? v2.sub : "rgba(255,255,255,0.8)"} numberOfLines={1}>
            {c.label}
          </Text>
          <Text
            fontSize={17}
            fontWeight="800"
            color={haze ? v2.hazeInk : "#fff"}
            letterSpacing={-0.3}
            numberOfLines={1}
            style={{ fontVariantNumeric: "tabular-nums" }}>
            {c.value}
          </Text>
        </YStack>
      ))}
    </XStack>
  );
};

export default AccountSummary;
